import { useEffect, useState } from 'react';
import { Button, Card, Tooltip, Tag, Dropdown, Badge, Pagination, Popconfirm, Modal, Input, Select, Typography } from 'antd';
import { Filter } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/DataTable';
import Icons from '@/components/third-party/Icons';
import { accessControlUtils } from '@/components/AccessControlUtils';
import ESignAuth from '@/components/ESignAuth';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import {
  get_production_demand_list,
  approve_production_demand,
  approve_demand_workflow_step,
  reject_demand_workflow_step,
} from '@/utils/api';
import { fmtDate, priorityTagColor, demandStatusTagColor, personName, canSubmitForApproval } from './helpers';
import CreateDemandForm from './CreateDemandModal';
import EditDemandModal from './EditDemandModal';
import DemandDetailDrawer from './DemandDetailDrawer';
import CreateLotsModal from './CreateLotsModal';
import WorkflowConfigModal from './WorkflowConfigModal';

const { Title } = Typography;

// ==============================|| PRODUCTION SCHEDULER ||============================== //

function ProductionScheduler({ initialDemandId = null, initialCreateOpen = false }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const accessData = accessControlUtils('Production Plan');
  const selectedModule = useSelector((state) => state.moduleView);
  const selectedButton = useSelector((state) => state.testProdView);
  const recipeTypeFlag = selectedButton === 'production';

  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [statusFilter, setStatusFilter] = useState(undefined);

  const [createOpen, setCreateOpen] = useState(!!initialCreateOpen);
  const [editDemand, setEditDemand] = useState(null);
  const [detailDemandId, setDetailDemandId] = useState(null);
  const [lotsDemand, setLotsDemand] = useState(null);
  const [workflowConfigOpen, setWorkflowConfigOpen] = useState(false);
  const [esignRecord, setEsignRecord] = useState(null);

  useEffect(() => {
    if (initialDemandId) {
      setDetailDemandId(Number(initialDemandId) || initialDemandId);
    }
  }, [initialDemandId]);

  const fetchDemands = () => {
    setLoading(true);
    get_production_demand_list(
      { page_number: pageNumber, ...(statusFilter && { status: statusFilter }) },
      (data) => {
        setLoading(false);
        if (data?.success) {
          setDemands(data.data || []);
          setTotal(data.count || 0);
          setPageSize(data.page_size || 15);
        }
      }
    );
  };

  useEffect(() => {
    fetchDemands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, statusFilter]);

  // `pending_step_id` is set once the workflow engine is live (backend not built yet —
  // see the "Backend needed" spec). Until then every demand falls back to the original
  // single-shot Draft -> Submitted -> Approved flow via approve_production_demand.
  const handleApprove = (record) => {
    if (record.pending_step_id) {
      approve_demand_workflow_step(record.pending_step_id, {}, (data) => {
        if (data?.success) {
          toast.success(
            data?.workflow_status === 'Approved'
              ? 'Demand fully approved'
              : 'Approved — sent to the next approver in the sequence'
          );
          fetchDemands();
        } else {
          toast.error(data?.message || 'Failed to approve demand');
        }
      });
      return;
    }
    approve_production_demand(record.demand_id, (data) => {
      if (data?.status) {
        toast.success('Demand approved');
        fetchDemands();
      } else {
        toast.error(data?.message || 'Failed to approve demand');
      }
    });
  };

  const handleReject = (record) => {
    let comment = '';
    Modal.warning({
      title: 'Rejection Comment',
      cancelText: 'Cancel',
      closable: true,
      maskClosable: true,
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Enter reason for rejection (required)"
          onChange={(e) => {
            comment = e.target.value;
          }}
        />
      ),
      onOk() {
        if (!comment.trim()) {
          toast.error('Please enter a reason for rejection');
          return Promise.reject();
        }
        reject_demand_workflow_step(record.pending_step_id, { comment: comment.trim() }, (data) => {
          if (data?.success) {
            toast.success('Demand rejected');
            fetchDemands();
          } else {
            toast.error(data?.message || 'Failed to reject demand');
          }
        });
      },
    });
  };

  const columns = [
    {
      key: 'batch_number',
      dataIndex: 'batch_number',
      title: 'Batch Number',
      defaultVisible: true,
      sortType: "string",
      width: 200,
      render: (v) => v || '—',
    },
    {
      key: 'product_name',
      dataIndex: 'product_name',
      title: 'Product',
      defaultVisible: true,
      sortType: "string",
      width: 150,
      render: (v) => v || '—',
    },
    {
      key: 'recipe_name',
      dataIndex: 'recipe_name',
      title: 'Recipe',
      defaultVisible: true,
      width: 150,
      render: (v) => v || '—',
    },
    {
      key: 'required_quantity',
      dataIndex: 'required_quantity',
      title: 'Required Qty (Batch Size)',
      defaultVisible: true,
      align: 'right',
      sortType: 'number',
      width: 150,
    },
    {
      key: 'priority',
      dataIndex: 'priority',
      title: 'Priority',
      defaultVisible: true,
      width: 100,
      render: (v) => <Tag color={priorityTagColor(v)}>{v}</Tag>,
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: 'Status',
      defaultVisible: true,
      width: 220,
      render: (v) => (
        <Tag color={demandStatusTagColor(v)} style={{ whiteSpace: 'normal' }}>
          {v}
        </Tag>
      ),
    },
    {
      key: 'pending_approver_role',
      dataIndex: 'pending_approver_role',
      title: 'Pending Approver',
      defaultVisible: true,
      width: 160,
      render: (v) => (v ? <Tag color="gold">{v}</Tag> : '—'),
    },
    {
      key: 'start_date',
      dataIndex: 'start_date',
      title: 'Start Date',
      defaultVisible: true,
      sortType: 'date',
      width: 120,
      render: fmtDate,
    },
    {
      key: 'end_date',
      dataIndex: 'end_date',
      title: 'End Date',
      defaultVisible: true,
      sortType: 'date',
      width: 120,
      render: fmtDate,
    },
    {
      key: 'created_by',
      dataIndex: 'created_by',
      title: 'Created By',
      defaultVisible: true,
      width: 150,
      render: personName,
    },
    {
      key: 'action',
      dataIndex: 'action',
      title: 'Action',
      defaultVisible: true,
      alwaysVisible: true,
      align: 'center',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-3">
          <Tooltip title="View">
            <span className="action-icon">
              <Icons.Eye
                onClick={() => setDetailDemandId(record.demand_id)}
                className="text-blue-600 w-5 h-5 cursor-pointer hover:text-blue-800"
              />
            </span>
          </Tooltip>
          {/* Editable only in Draft/Rejected — same window canSubmitForApproval uses, since
              once Submitted/Approved the demand's already moving through (or past) approval
              and its master batch is already scheduled off the original values. */}
          {accessData?.update_access && canSubmitForApproval(record.status) && (
            <Tooltip title="Edit">
              <span className="action-icon">
                <Icons.Pencil
                  onClick={() => setEditDemand(record)}
                  className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800"
                />
              </span>
            </Tooltip>
          )}
          {accessData?.update_access &&
            (record.pending_step_id || record.status === 'Submitted') &&
            (record.pending_step_esign_required ? (
              <Tooltip title="Approve (E-Signature Required)">
                <span className="action-icon">
                  <Icons.CircleCheck
                    onClick={() => setEsignRecord(record)}
                    className="text-green-600 w-5 h-5 cursor-pointer hover:text-green-800"
                  />
                </span>
              </Tooltip>
            ) : (
              <Popconfirm
                title="Approve this production demand?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => handleApprove(record)}
              >
                <Tooltip title="Approve">
                  <span className="action-icon">
                    <Icons.CircleCheck className="text-green-600 w-5 h-5 cursor-pointer hover:text-green-800" />
                  </span>
                </Tooltip>
              </Popconfirm>
            ))}
          {/* Reject only exists on the workflow-engine path (pending_step_id) — the
              original simple Draft/Submitted/Approved flow has no reject endpoint. */}
          {accessData?.update_access && record.pending_step_id && (
            <Tooltip title="Reject">
              <span className="action-icon">
                <Icons.XCircle
                  onClick={() => handleReject(record)}
                  className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800"
                />
              </span>
            </Tooltip>
          )}
          {accessData?.create_access && (
            <Tooltip title="Create Batches">
              <span className="action-icon">
                <Icons.Layers
                  onClick={() => setLotsDemand(record)}
                  className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800"
                />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
  ];

  return (
    <Card
      className="border border-gray-300 shadow-md"
      bordered={false}
      classNames={{ body: 'page-card-scroll' }}
      style={{ height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column', backgroundColor: cardBgColor }}
      styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
      title={
        <PageHeader
          compact
          icon={<Icons.Layers size={22} />}
          title={t('Master & Lot')}
          subtitle={`${total} demand${total === 1 ? '' : 's'}`}
          description="Production demands: master batches, their lots and the approval each needs."
          filters={
            <Select
              aria-label="Filter by status"
              value={statusFilter || 'all'}
              style={{ width: 170 }}
              options={STATUS_OPTIONS}
              onChange={(key) => {
                setStatusFilter(key === 'all' ? undefined : key);
                setPageNumber(1);
              }}
            />
          }
          secondary={[
            { key: 'workflow', label: 'Approval workflow', icon: <Icons.Workflow className="w-4 h-4" />, onClick: () => setWorkflowConfigOpen(true), hidden: !accessData?.update_access, tooltip: 'Configure the approval workflow' },
          ]}
          extra={
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />
                  <span className="text-sm font-medium" style={{ color: textColor }}>Master</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-orange-500 inline-block" />
                  <span className="text-sm font-medium" style={{ color: textColor }}>Lot</span>
                </div>
              </div>
              {createOpen && accessData?.create_access && (
                <Button icon={<Icons.X className="w-4 h-4" />} onClick={() => setCreateOpen(false)}>
                  Close form
                </Button>
              )}
            </div>
          }
          primary={{ label: 'Create Demand', icon: <Icons.Plus className="w-4 h-4" />, onClick: () => setCreateOpen(true), hidden: !accessData?.create_access || createOpen }}
        />
      }
    >
      <CreateDemandForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchDemands}
        selectedModule={selectedModule}
        recipeTypeFlag={recipeTypeFlag}
      />

      <div className="planner-table">
      <DataTable
        tableKey="productionSchedulerTable"
        serverSide
        searchable
        searchPlaceholder="Search demands…"
        columnConfig
        showCount={false}
        bordered
        loading={loading}
        columns={columns}
        data={demands?.map((item) => ({ ...item, key: item.demand_id }))}
        height={null}
        minHeight={320}
        scrollX={1660}
        emptyMessage="No production demands found"
        pagination={{
          current: pageNumber,
          pageSize,
          total,
          showSizeChanger: false,
          showTotal: (n) => `Total ${n} Demands`,
          onChange: (value) => setPageNumber(value),
        }}
      />
      </div>

      <EditDemandModal
        open={!!editDemand}
        onClose={() => setEditDemand(null)}
        onUpdated={fetchDemands}
        demand={editDemand}
      />

      <DemandDetailDrawer
        open={!!detailDemandId}
        onClose={() => setDetailDemandId(null)}
        demandId={detailDemandId}
        accessData={accessData}
        onChanged={fetchDemands}
      />

      <CreateLotsModal
        open={!!lotsDemand}
        onClose={() => setLotsDemand(null)}
        demand={lotsDemand}
        canCreate={accessData?.create_access}
        onLotsChanged={fetchDemands}
      />

      <WorkflowConfigModal open={workflowConfigOpen} onClose={() => setWorkflowConfigOpen(false)} />

      <ESignAuth
        visible={!!esignRecord}
        onCancel={() => setEsignRecord(null)}
        onSubmit={() => {
          handleApprove(esignRecord);
          setEsignRecord(null);
        }}
        req={{
          batch_number: esignRecord?.batch_number,
          action_type: 'Approve',
          workflow_step_id: esignRecord?.pending_step_id,
        }}
      />
    </Card>
  );
}

export default ProductionScheduler;
